import { fmtDateTime, fmtMoney } from './format';

// The history of a work order, as the customer reads it.
//
// The server stores each step as a KIND and its numbers (server/requestUpdates.js)
// rather than a sentence, because the portal is read in twelve languages. This
// is where a kind becomes a line of text in the language they chose.
//
// It also rebuilds a history for work orders that predate the log. Every job
// already carried the dates — when it was submitted, when the estimate was
// written, when the work was finished — they were just never shown. Deriving
// them means nobody opens their work order to an empty screen, which is the
// worst possible answer to "has anything happened with my job?".

// Kinds we can reconstruct from fields that were always on the record, in the
// order they happen. `at` is the only thing that has to be real — an entry with
// no date can't be placed on a timeline, so it isn't invented.
const derive = (req) => {
  const out = [];
  if (req.createdAt) out.push({ at: req.createdAt, kind: 'submitted' });
  if (req.estimate?.submittedAt) out.push({ at: req.estimate.submittedAt, kind: 'estimate-sent', amount: req.estimate.amount });
  // No timestamp of its own: an approved estimate is dated by the estimate it
  // approved, which puts it in the right place without pretending to a precision
  // the record doesn't have.
  if (req.estimateStatus === 'approved' && req.estimate?.submittedAt) {
    out.push({ at: req.estimate.submittedAt, kind: 'estimate-approved', amount: req.estimate.amount });
  }
  if (req.completedAt) out.push({ at: req.completedAt, kind: 'completed', note: req.completionNote });
  if (req.invoice?.submittedAt) out.push({ at: req.invoice.submittedAt, kind: 'invoiced', amount: req.invoice.amount });
  return out;
};

// The order these things happen in, for entries that share a timestamp.
//
// Two can: an approval derived from the estimate it approved is dated by that
// estimate, because the record holds no separate moment for it. Sorting on the
// date alone then leaves it a coin toss whether the history reads "approved,
// then sent" — which is nonsense to the person reading it.
const STEP = ['submitted', 'appointment-set', 'estimate-sent', 'estimate-declined', 'estimate-approved', 'work-order', 'assigned', 'completed', 'invoiced', 'invoice-approved', 'paid'];

// Stored entries win: they carry the moment the thing actually happened. A
// derived entry only fills in for a kind that was never logged, which is every
// kind on a job that ran before the log existed.
export function timelineFor(request) {
  if (!request) return [];
  const stored = (request.updates || []).filter(u => u?.at && u?.kind);
  const kinds = new Set(stored.map(u => u.kind));
  const merged = [...stored, ...derive(request).filter(u => !kinds.has(u.kind))];
  // Newest first: the customer is opening this to find out what is new, not to
  // read a biography from the beginning.
  return merged.sort((a, b) =>
    new Date(b.at) - new Date(a.at) || STEP.indexOf(b.kind) - STEP.indexOf(a.kind));
}

// Is any of it newer than the last time they looked?
//
// A work order they have never opened does NOT count as new on its own — they
// submitted it themselves thirty seconds ago, and a "new update" dot on your
// own submission is noise. So the first entry, the submission, is skipped.
export function hasNewUpdates(request) {
  const entries = timelineFor(request).filter(u => u.kind !== 'submitted');
  if (!entries.length) return false;
  const seen = request.homeownerSeenAt ? new Date(request.homeownerSeenAt) : null;
  return !seen || new Date(entries[0].at) > seen;
}

export const isNew = (entry, request) =>
  entry.kind !== 'submitted'
  && (!request.homeownerSeenAt || new Date(entry.at) > new Date(request.homeownerSeenAt));

// One line, in their language. `t` comes from the caller so this stays a plain
// function — it is used inside a render, not around it.
export function updateText(entry, t) {
  const money = entry.amount != null ? fmtMoney(entry.amount) : '';
  switch (entry.kind) {
    case 'submitted': return t('upd.submitted');
    case 'appointment-set': return t('upd.appointment').replace('{when}', entry.when ? fmtDateTime(entry.when) : '');
    case 'estimate-sent': return t('upd.estimateSent').replace('{amount}', money);
    // Approved BY US, on their behalf — telling someone "you approved this"
    // when they never saw a screen is how an app loses their trust.
    case 'estimate-approved': return (entry.by === 'admin'
      ? t('upd.estimateApprovedByUs')
      : t('upd.estimateApproved')).replace('{amount}', money);
    case 'estimate-declined': return entry.by === 'admin' ? t('upd.estimateDeclinedByUs') : t('upd.estimateDeclined');
    case 'work-order': return entry.note
      ? t('upd.workOrder').replace('{number}', entry.note)
      : t('upd.workOrderPlain');
    case 'assigned': return entry.note
      ? t('upd.assignedNamed').replace('{name}', entry.note)
      : t('upd.assigned');
    case 'completed': return t('upd.completed');
    case 'invoiced': return t('upd.invoiced').replace('{amount}', money);
    // Its own line, not 'paid'. Reusing 'paid' for an approval is what told
    // a customer their payment had arrived while they still owed all of it.
    case 'invoice-approved': return t('upd.invoiceApproved').replace('{amount}', money);
    case 'paid': return t('upd.paid');
    default: return '';
  }
}
